import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AIConversations = ({ user, onBack }) => {
  const [categories, setCategories] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchScenarios(selectedCategory);
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/conversations-ai/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchScenarios = async (category) => {
    try {
      const response = await axios.get(`${API}/conversations-ai/scenarios?category=${category}`);
      setScenarios(response.data.scenarios);
    } catch (error) {
      console.error('Error fetching scenarios:', error);
    }
  };

  const startConversation = async (scenario) => {
    setLoading(true);
    try {
      const scenarioId = scenario.title.replace(/\s+/g, '-').toLowerCase();
      const response = await axios.post(`${API}/conversations-ai/sessions/start`, null, {
        params: {
          scenario_id: scenarioId,
          user_id: user.id
        }
      });
      
      setActiveSession(response.data);
      setSelectedScenario(scenario);
      setMessages([{
        role: 'assistant',
        content: response.data.initial_message,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      console.error('Error starting conversation:', error);
      alert('Failed to start conversation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!currentMessage.trim() || !activeSession) return;

    const userMsg = {
      role: 'user',
      content: currentMessage,
      timestamp: new Date().toISOString()
    };

    setMessages([...messages, userMsg]);
    setCurrentMessage('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/conversations-ai/sessions/${activeSession.session_id}/message`,
        null,
        {
          params: {
            user_id: user.id,
            message: currentMessage
          }
        }
      );

      const aiMsg = {
        role: 'assistant',
        content: response.data.ai_response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, aiMsg]);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const endConversation = async () => {
    if (!activeSession) return;

    setLoading(true);
    try {
      await axios.post(
        `${API}/conversations-ai/sessions/${activeSession.session_id}/complete`,
        null,
        {
          params: { user_id: user.id }
        }
      );

      // Get feedback
      const feedbackResponse = await axios.post(
        `${API}/conversations-ai/sessions/${activeSession.session_id}/feedback`,
        null,
        {
          params: { user_id: user.id }
        }
      );

      setFeedback(feedbackResponse.data.feedback);
      setShowFeedback(true);
    } catch (error) {
      console.error('Error ending conversation:', error);
      // Still show basic feedback
      setShowFeedback(true);
    } finally {
      setLoading(false);
    }
  };

  const resetConversation = () => {
    setActiveSession(null);
    setSelectedScenario(null);
    setMessages([]);
    setShowFeedback(false);
    setFeedback(null);
  };

  // Category Selection View
  if (!selectedCategory) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-800">AI-Powered Virtual Conversations</h2>
            <p className="text-gray-600 mt-2">Practice real-life scenarios with AI-powered personas</p>
          </div>
          {onBack && (
            <button
              onClick={onBack}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              ← Back
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {categories.map((category) => (
            <div
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow border-2 border-transparent hover:border-blue-500"
            >
              <div className="flex items-start space-x-4">
                <div className="text-5xl">{category.icon}</div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{category.name}</h3>
                  <p className="text-gray-600 mb-4">{category.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-blue-600 font-medium">
                      {category.scenario_count} scenarios available
                    </span>
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                      Explore →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-blue-50 rounded-lg p-6">
          <h3 className="text-lg font-bold text-blue-900 mb-2">💡 How It Works</h3>
          <ul className="space-y-2 text-blue-800">
            <li>• Choose a conversation category that interests you</li>
            <li>• Select a specific scenario to practice</li>
            <li>• Have a natural conversation with an AI-powered persona</li>
            <li>• Receive personalized feedback and scoring</li>
            <li>• Try different responses to improve your skills</li>
          </ul>
        </div>
      </div>
    );
  }

  // Scenario Selection View
  if (!activeSession && !showFeedback) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <button
            onClick={() => setSelectedCategory(null)}
            className="text-blue-600 hover:text-blue-700 mb-4"
          >
            ← Back to Categories
          </button>
          <h2 className="text-3xl font-bold text-gray-800">
            {categories.find(c => c.id === selectedCategory)?.name} Scenarios
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {scenarios.map((scenario, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-xl font-bold text-gray-800">{scenario.title}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      scenario.difficulty_level === 'beginner' ? 'bg-green-100 text-green-800' :
                      scenario.difficulty_level === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {scenario.difficulty_level}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4">{scenario.description}</p>
                  
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">You'll practice with:</p>
                    <p className="text-sm text-blue-600">🤖 {scenario.ai_persona}</p>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Learning Objectives:</p>
                    <ul className="list-disc list-inside space-y-1">
                      {scenario.learning_objectives.map((obj, i) => (
                        <li key={i} className="text-sm text-gray-600">{obj}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>⏱️ ~{scenario.estimated_duration} minutes</span>
                    <span>•</span>
                    <span>📊 Auto-graded</span>
                  </div>
                </div>

                <button
                  onClick={() => startConversation(scenario)}
                  disabled={loading}
                  className="ml-4 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 whitespace-nowrap"
                >
                  {loading ? 'Starting...' : 'Start Conversation'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Active Conversation View
  if (activeSession && !showFeedback) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Header */}
          <div className="bg-blue-600 text-white p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold">{selectedScenario?.title}</h3>
                <p className="text-sm text-blue-100">Conversing with: {selectedScenario?.ai_persona}</p>
              </div>
              <button
                onClick={endConversation}
                className="px-4 py-2 bg-blue-700 hover:bg-blue-800 rounded-lg text-sm"
              >
                End & Get Feedback
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="h-[500px] overflow-y-auto p-6 bg-gray-50 space-y-4">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xs md:max-w-md px-4 py-3 rounded-lg ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-800 border border-gray-200'
                  }`}
                >
                  <p className="text-sm">{msg.content}</p>
                  <p className={`text-xs mt-1 ${
                    msg.role === 'user' ? 'text-blue-100' : 'text-gray-500'
                  }`}>
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 border border-gray-200 px-4 py-3 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <div className="animate-pulse">💭</div>
                    <span className="text-sm text-gray-600">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t">
            <div className="flex space-x-2">
              <input
                type="text"
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !loading && sendMessage()}
                placeholder="Type your response..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              />
              <button
                onClick={sendMessage}
                disabled={loading || !currentMessage.trim()}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                Send
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              💡 Tip: Be natural and authentic in your responses. The AI will adapt to your communication style.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Feedback View
  if (showFeedback) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Conversation Complete!</h2>
            <p className="text-gray-600">Here's your feedback</p>
          </div>

          {feedback && (
            <div className="space-y-6">
              {/* Score */}
              <div className="bg-blue-50 rounded-lg p-6 text-center">
                <p className="text-sm text-gray-600 mb-2">Your Score</p>
                <p className="text-5xl font-bold text-blue-600">{feedback.score}/100</p>
              </div>

              {/* Strengths */}
              {feedback.strengths && feedback.strengths.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-green-600 mb-3">✅ Strengths</h3>
                  <ul className="space-y-2">
                    {feedback.strengths.map((strength, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <span className="text-green-500 mt-1">•</span>
                        <span className="text-gray-700">{strength}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Areas for Improvement */}
              {feedback.improvements && feedback.improvements.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-orange-600 mb-3">💡 Areas for Growth</h3>
                  <ul className="space-y-2">
                    {feedback.improvements.map((improvement, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <span className="text-orange-500 mt-1">•</span>
                        <span className="text-gray-700">{improvement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="mt-8 flex space-x-4">
            <button
              onClick={() => startConversation(selectedScenario)}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Try Again
            </button>
            <button
              onClick={resetConversation}
              className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Choose Another Scenario
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AIConversations;
